import type { JSX } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  AssistantTurn,
  Citation,
  HealthResponse,
  ImageAttachment,
  ResolutionStepState,
  SuggestionAction,
  ThreadSummary,
  Turn,
  UserTurn
} from "@shared/types";
import {
  fetchHealth,
  fetchSessions,
  fetchSessionTurns,
  deleteSession,
  postHandoff,
  postFeedback,
  fetchSuggestions,
  mutateResolutionStep,
  streamChat,
  getMentionableAssistants,
  getAssistantCard,
  type AssistantCardData,
  type MentionableAssistant
} from "./lib/api";
import type { OutgoingImage } from "./lib/api";
import { deriveTitle, mergeInflight } from "./lib/sessionMerge";
import { Topbar } from "./components/chat/Topbar";
import { Sidebar } from "./components/chat/Sidebar";
import { Composer } from "./components/chat/Composer";
import { getSelectedModelId } from "./components/chat/ModelSelector";
import { RightRail } from "./components/chat/RightRail";
import { AssistantBubble, UserBubble } from "./components/chat/MessageBubble";
import { TicketsPanel } from "./components/chat/TicketsPanel";
import { LoomPanel } from "./components/loom/LoomPanel";
import { Login } from "./components/chat/Login";
import { FeedbackModal } from "./components/chat/FeedbackModal";
import { getToken, getUser, clearToken, type AuthUser } from "./lib/auth";
import { BrandLogo } from "./components/BrandLogo";
import { IconArrowDown, IconArrowRight, IconFile, IconGrid, IconSpark } from "./components/icons";
import { PlaybookOverlay, hasPlaybook } from "./playbook/PlaybookOverlay";
import { MeetingView } from "./meeting/MeetingView";
import { PersonalKbPanel } from "./personal-kb/PersonalKbPanel";
import { ArtifactCanvas } from "./artifacts/ArtifactCanvas";
import { SharedArtifactView } from "./artifacts/SharedArtifact";
import { ArtifactLibraryPanel } from "./artifacts/ArtifactLibraryPanel";
import { feishuOAuthApi } from "./lib/feishuOAuthApi";
import { ReplaySessionView } from "./playbook/ReplaySessionView";
import type { PlaybookReplayCase } from "./playbook/types";
import { usePresence } from "./hooks/usePresence";

/**
 * Real chat shell. All data — sessions, threads, turns — comes from
 * the backend. SSE deltas are routed by assistantTurnId so switching
 * sessions during a stream cannot pollute another thread.
 */

const MAX_INPUT_CHARS = 4000;

// Role-aware new-chat welcome. End users (customers) see the support framing;
// internal staff (agent / admin) see the personal-assistant framing.
const WELCOME_END_USER =
  "您好，我是小环，织灵的客服助手。关于织灵的使用、功能咨询、异常反馈等问题，都可以向我咨询。我会尽力为您解答，必要时为您转接人工客服。";
const WELCOME_STAFF =
  "您好，我是小环，你的私人工作助理。织灵的使用与排查、研发与技术工作上的问题，尽管问我——我会帮你最快找到答案，而且越用越懂你。答得好就点个赞、不对就点踩并说说原因；也欢迎随时把你想要的功能告诉我，让我长成你最趁手的助手。";

// Static fallback for the empty-state CTAs (iron law #08). The live list is
// fetched from /api/suggestions (§2-5) — KB-grounded starter questions — and
// falls back to these if that is unavailable. Clicking sends the query.
const FALLBACK_SUGGESTIONS = ["织灵能做什么？", "怎么开始使用织灵？", "如何管理我的织灵账号？"];
// Always-pinned first starter chip on the new-session home screen: invites the
// user to ask 小环 about itself, which renders the capability showcase card.
// Pinned (not just a fallback) so it shows even when KB-grounded suggestions
// from /api/suggestions are present.
const PINNED_SUGGESTION = "小环能做什么？有哪些功能？";

/** The user question an assistant turn answered: nearest preceding user turn. */
function precedingQuestionAt(turns: Turn[], idx: number): string {
  for (let i = idx - 1; i >= 0; i--) {
    const t = turns[i];
    if (t !== undefined && t.role === "user") return t.text;
  }
  return "";
}

interface HealthState {
  loaded: boolean;
  health: HealthResponse | null;
  downReason: string | null;
}

export function App(): JSX.Element {
  // Live presence heartbeat (#228 Phase 2): pings while this tab is open so the
  // admin console can show online/offline. Best-effort; no-op without a token.
  usePresence();
  const [healthState, setHealthState] = useState<HealthState>({
    loaded: false,
    health: null,
    downReason: null
  });
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  // Optimistic sidebar entry for a session whose first turn is still streaming
  // and therefore not yet in the server's session list. Pinned so a chat is
  // always reachable from the left nav the moment it starts (#225, #175).
  const [inflightThread, setInflightThread] = useState<ThreadSummary | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [activeCitation, setActiveCitation] = useState<Citation | null>(null);
  const [showTickets, setShowTickets] = useState(false);
  const [showLoom, setShowLoom] = useState(false);
  const [showMaterials, setShowMaterials] = useState(false);
  // Artifacts (FEATURE_ARTIFACTS · #7 · 7a): the 作品库 modal and the side canvas
  // that shows one generated artifact's markdown (opened from a chat card or a
  // Library tile).
  const [showLibrary, setShowLibrary] = useState(false);
  const [activeArtifactId, setActiveArtifactId] = useState<string | null>(null);
  // Wide reading mode for the artifact canvas (split view: the chat compresses
  // left, the canvas takes more of the screen). Reset whenever the canvas closes.
  const [canvasExpanded, setCanvasExpanded] = useState(false);

  // P4c: dispatch a next-steps connect/upload chip. upload → open 我的资料库;
  // connect (feishu) → start the per-user OAuth flow.
  const onSuggestionAction = useCallback((action: SuggestionAction): void => {
    if (action.type === "upload") setShowMaterials(true);
    else if (action.type === "connect" && action.provider === "feishu") {
      void feishuOAuthApi.begin().catch(() => undefined);
    }
  }, []);

  // Feishu OAuth callback bounce (P4b): the server redirects back with
  // ?feishu=connected|error — open 我的资料库 so the user sees the result, then
  // strip the query so a reload does not reopen it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const feishu = params.get("feishu");
    if (feishu === null) return;
    setShowMaterials(true);
    params.delete("feishu");
    const qs = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
  }, []);
  const [showFeedback, setShowFeedback] = useState(false);
  const [playbookOpen, setPlaybookOpen] = useState(false);
  // Meeting capture surface (FEATURE_MEETING); a main-column view, not a route.
  const [meetingOpen, setMeetingOpen] = useState(false);
  // PH-1: a meeting "问这场会" waiting for its conversation to load before the
  // question is sent into it (avoids the onSend stale-closure on activeSessionId).
  const [pendingMeetingAsk, setPendingMeetingAsk] = useState<{ sid: string; q: string } | null>(null);
  // The active worked-example replay session opened from the playbook, or null.
  // Rendered as a full-screen sibling overlay (the app has no router), gated on
  // the replay feature flag (health `features.replay`, FEATURE_IA_REPLAY).
  const [replaySession, setReplaySession] = useState<{
    replays: PlaybookReplayCase[];
    title: string;
  } | null>(null);
  const [streaming, setStreaming] = useState(false);
  // Off-canvas sidebar state (mobile only; ignored on desktop where the
  // sidebar is a permanent column).
  const [navOpen, setNavOpen] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(getUser());
  // Home-screen starter suggestions (§2-5): KB-grounded, fetched on mount, with
  // a static fallback so the empty state always has CTAs.
  const [suggestions, setSuggestions] = useState<string[]>(FALLBACK_SUGGESTIONS);
  // "Jump to latest" affordance: shown when the transcript is scrolled up far
  // enough that newer content sits below the fold.
  const [showJump, setShowJump] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // Tracks which assistant turn id the currently active stream is
  // writing into. Used by onDelta to refuse cross-session pollution
  // when the user switches threads mid-stream.
  const streamingTurnIdRef = useRef<string | null>(null);
  const streamingSessionIdRef = useRef<string | null>(null);
  // Mirror of activeSessionId that is always current within the same tick.
  // updateTurnById must not read the activeSessionId closure: onStart sets the
  // session id via setState, but in-flight stream callbacks captured the old
  // closure value and would otherwise drop every delta of a new session.
  const activeSessionIdRef = useRef<string | null>(null);
  // The transcript scroll container, plus whether the user is currently parked
  // at the bottom (so streaming can follow without yanking them up mid-read).
  const threadRef = useRef<HTMLDivElement | null>(null);
  const atBottomRef = useRef(true);
  // Always-current mirror of `turns` so stable callbacks (onRate) can read the
  // rated turn's question/answer without re-creating on every turn.
  const turnsRef = useRef<Turn[]>([]);
  // Live buffer of the streaming session's full turn list. Stream events write
  // here regardless of which thread is on screen, so navigating away to another
  // session and back restores the in-flight chat instead of fetching an empty
  // (not-yet-persisted) transcript (#225). It is reflected into the visible
  // `turns` only while the streaming session is the active one.
  const streamingTurnsRef = useRef<Turn[]>([]);

  // How close to the bottom (px) still counts as "at bottom" — covers sub-pixel
  // rounding and a small breathing margin so the jump button doesn't flicker.
  const BOTTOM_THRESHOLD_PX = 80;

  const scrollThreadToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const el = threadRef.current;
    if (el !== null) el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const onThreadScroll = useCallback(() => {
    const el = threadRef.current;
    if (el === null) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceFromBottom < BOTTOM_THRESHOLD_PX;
    atBottomRef.current = atBottom;
    // Only offer the jump button when there is meaningfully more below the fold.
    setShowJump(!atBottom && el.scrollHeight - el.clientHeight > BOTTOM_THRESHOLD_PX);
  }, []);

  // Follow the stream: keep the view pinned to the latest content as it grows,
  // but only when the user is already at the bottom — never pull them down while
  // they are reading earlier messages.
  useEffect(() => {
    if (atBottomRef.current) scrollThreadToBottom("auto");
  }, [turns, scrollThreadToBottom]);

  const loadData = useCallback(() => {
    void fetchSessions().then(setThreads);
    // KB-grounded home suggestions (§2-5); keep the static fallback on empty.
    void fetchSuggestions().then((s) => {
      if (s.length > 0) setSuggestions(s);
    });
  }, []);

  const handleLogout = useCallback(() => {
    clearToken();
    setUser(null);
    setThreads([]);
    setInflightThread(null);
    streamingTurnsRef.current = [];
    setTurns([]);
    setActiveSessionId(null);
    setNeedsLogin(true);
  }, []);

  useEffect(() => {
    void fetchHealth().then((r) => {
      setHealthState({
        loaded: true,
        health: r.kind === "ok" ? r.health : null,
        downReason: r.kind === "down" ? r.reason : null
      });
      // When the server enforces auth (#49) and we have no token, show login.
      const authEnabled = r.kind === "ok" && r.health.authEnabled === true;
      if (authEnabled && getToken() === null) {
        setNeedsLogin(true);
      } else {
        loadData();
      }
    });
    return () => {
      abortRef.current?.abort();
    };
  }, [loadData]);

  useEffect(() => {
    activeSessionIdRef.current = activeSessionId;
  }, [activeSessionId]);

  useEffect(() => {
    turnsRef.current = turns;
  }, [turns]);

  // Monotonic guard so a slow fetch for an earlier-selected thread cannot
  // overwrite the turns of a thread the user selected afterwards (codex
  // 2026-06-02 §3): only the most recent loadSession call may commit, and only
  // if its thread is still the active one.
  const loadSeqRef = useRef(0);
  const loadSession = useCallback(async (id: string) => {
    const seq = ++loadSeqRef.current;
    setActiveSessionId(id);
    activeSessionIdRef.current = id;
    setActiveCitation(null);
    // Re-selecting the session whose first turn is still streaming: it is not in
    // Postgres yet, so a fetch would 404 → empty transcript. Restore the live
    // turns from the buffer instead, and keep following the stream (#225).
    if (id === streamingSessionIdRef.current) {
      setTurns(streamingTurnsRef.current);
      return;
    }
    const next = await fetchSessionTurns(id);
    if (seq === loadSeqRef.current && activeSessionIdRef.current === id) {
      setTurns(next);
    }
  }, []);

  const onSelectThread = useCallback(
    (id: string) => {
      void loadSession(id).catch((err) => {
        console.error("load session failed", err);
      });
    },
    [loadSession]
  );

  const onNewThread = useCallback(() => {
    setActiveSessionId(null);
    activeSessionIdRef.current = null;
    setTurns([]);
    setActiveCitation(null);
  }, []);

  // Reflect the streaming buffer into the visible transcript, but only while the
  // streaming session is the one on screen — the single guard that keeps a
  // background stream from polluting another thread (#225).
  const commitStreamTurns = useCallback(() => {
    if (streamingSessionIdRef.current === activeSessionIdRef.current) {
      setTurns(streamingTurnsRef.current);
    }
  }, []);

  const updateTurnById = useCallback(
    (turnId: string, sessionId: string | null, update: (t: AssistantTurn) => AssistantTurn) => {
      // Ignore deltas that belong to a superseded stream (e.g. a stale request
      // after the user started a new one). Refs (not the activeSessionId
      // closure) so a new session's first deltas are not dropped.
      if (sessionId !== null && sessionId !== streamingSessionIdRef.current) return;
      // Always advance the live buffer so the in-flight chat survives a detour
      // to another thread and can be restored on return (#225).
      streamingTurnsRef.current = streamingTurnsRef.current.map((t) =>
        t.role === "assistant" && t.id === turnId ? update(t) : t
      );
      commitStreamTurns();
    },
    [commitStreamTurns]
  );

  const onSend = useCallback(
    (text: string, images?: OutgoingImage[], mention?: string, regenerateArtifactId?: string) => {
      if (streaming) return;
      setStreaming(true);
      const abort = new AbortController();
      abortRef.current = abort;
      streamingSessionIdRef.current = activeSessionId;

      // Optimistic render (#81): show the user's message and the "小环正在思考"
      // indicator the instant they hit send, instead of waiting ~2s for the
      // backend's `start` event. These placeholders carry temp ids; onStart
      // reconciles them to the server-authoritative turns (same id → seamless).
      const tempUserId = `u_local_${Date.now()}`;
      const tempAssistantId = `a_local_${Date.now()}`;
      const optimisticImages: ImageAttachment[] | undefined = images?.map((im) => ({
        dataUrl: `data:${im.mimeType};base64,${im.data}`,
        mimeType: im.mimeType
      }));
      const optimisticUser: UserTurn = {
        id: tempUserId,
        role: "user",
        text,
        ...(optimisticImages !== undefined ? { images: optimisticImages } : {}),
        timestamp: Date.now()
      };
      const optimisticPending: AssistantTurn = {
        id: tempAssistantId,
        role: "assistant",
        template: "KB_HIT",
        directAnswer: "",
        citations: [],
        toolCalls: [],
        timestamp: Date.now(),
        streaming: true
      };
      streamingTurnIdRef.current = tempAssistantId;
      // Seed the live buffer (current history + the two optimistic turns) and
      // mirror it into view. From here the buffer is the source of truth for the
      // streaming session, so a detour to another thread can restore it (#225).
      streamingTurnsRef.current = [...turnsRef.current, optimisticUser, optimisticPending];
      commitStreamTurns();

      const run = async (): Promise<void> => {
        await streamChat(
          activeSessionId,
          text,
          {
            onStart: ({ sessionId, userTurn, assistantTurnId, assistant }) => {
              if (assistant !== undefined) {
                setServingAssistant(assistant);
                // The pill belongs to THIS session: the per-thread reset must
                // not fire on the null → server-assigned-id transition of a
                // brand-new chat's first turn (it is the same thread).
                pillSessionRef.current = sessionId;
              }
              // The session this send was viewing when it started (null for a
              // brand-new chat). Used to decide whether the user is still on it.
              const startedFrom = streamingSessionIdRef.current;
              const stillViewingNewChat = activeSessionIdRef.current === startedFrom;
              streamingTurnIdRef.current = assistantTurnId;
              streamingSessionIdRef.current = sessionId;
              // Adopt the server's session id as active only if the user has not
              // navigated to another thread during startup — otherwise we would
              // yank them back out of the thread they deliberately opened (#225).
              if (stillViewingNewChat) {
                setActiveSessionId(sessionId);
                activeSessionIdRef.current = sessionId;
              }
              const pending: AssistantTurn = {
                id: assistantTurnId,
                role: "assistant",
                template: "KB_HIT",
                directAnswer: "",
                citations: [],
                toolCalls: [],
                timestamp: Date.now(),
                streaming: true
              };
              // Reconcile the optimistic placeholders to the server's turns in
              // the live buffer (idempotent: onStart precedes any delta).
              streamingTurnsRef.current = [
                ...streamingTurnsRef.current.filter(
                  (t) => t.id !== tempUserId && t.id !== tempAssistantId
                ),
                userTurn,
                pending
              ];
              commitStreamTurns();
              // Pin the new session into the sidebar immediately so it is always
              // reachable from the left nav, even mid-stream (#225, #175).
              setInflightThread({
                id: sessionId,
                title: deriveTitle(text),
                lastTurnAt: Date.now(),
                turnCount: streamingTurnsRef.current.length
              });
            },
            onDelta: (chunk, assistantTurnId) => {
              updateTurnById(assistantTurnId, streamingSessionIdRef.current, (last) => ({
                ...last,
                directAnswer: last.directAnswer + chunk
              }));
            },
            onTool: (tool, assistantTurnId) => {
              updateTurnById(assistantTurnId, streamingSessionIdRef.current, (last) => {
                const i = last.toolCalls.findIndex((c) => c.id === tool.id);
                const toolCalls =
                  i >= 0
                    ? last.toolCalls.map((c) => (c.id === tool.id ? tool : c))
                    : [...last.toolCalls, tool];
                return { ...last, toolCalls };
              });
            },
            onCitations: (citations, assistantTurnId) => {
              updateTurnById(assistantTurnId, streamingSessionIdRef.current, (last) => ({
                ...last,
                citations
              }));
            },
            onWidget: (widget, assistantTurnId) => {
              updateTurnById(assistantTurnId, streamingSessionIdRef.current, (last) => {
                const i = (last.widgets ?? []).findIndex((w) => w.id === widget.id);
                const widgets =
                  i >= 0
                    ? (last.widgets ?? []).map((w) => (w.id === widget.id ? widget : w))
                    : [...(last.widgets ?? []), widget];
                return { ...last, widgets };
              });
            },
            onBlock: (block, assistantTurnId) => {
              updateTurnById(assistantTurnId, streamingSessionIdRef.current, (last) => {
                const i = (last.blocks ?? []).findIndex((b) => b.id === block.id);
                const blocks =
                  i >= 0
                    ? (last.blocks ?? []).map((b) => (b.id === block.id ? block : b))
                    : [...(last.blocks ?? []), block];
                return { ...last, blocks };
              });
            },
            onAssistant: (a) => {
              // Transfer takeover: pill switches mid-turn; remember the
              // previous assistant so 换回 can stage an @ back to it.
              setServingAssistant((prev) => {
                if (prev !== null && prev.profileId !== a.profileId) setReturnAssistant(prev);
                return a;
              });
              pillSessionRef.current = streamingSessionIdRef.current;
            },
            onRunnable: (assistantTurnId) => {
              // LOOM_LIVE: the model marked this answer runnable in the user's 织灵;
              // gates the Try-it-yourself CTA (single source of truth, no classifier).
              updateTurnById(assistantTurnId, streamingSessionIdRef.current, (last) => ({
                ...last,
                runnable: true
              }));
            },
            onDone: ({ assistantTurnId, responseId }) => {
              updateTurnById(assistantTurnId, streamingSessionIdRef.current, (last) => ({
                ...last,
                streaming: false,
                // Stable key for joining a later rating to this turn's raw step
                // record (openspec/rating-step-join.md).
                ...(responseId !== undefined ? { responseId } : {})
              }));
              setStreaming(false);
              streamingTurnIdRef.current = null;
              // Keep streamingSessionIdRef set: the buffer stays the freshest
              // copy of this session, so re-selecting it restores the final
              // turns even before the fire-and-forget server persist lands.
              const sid = streamingSessionIdRef.current;
              void fetchSessions().then((list) => {
                setThreads(list);
                // Drop the optimistic pin only once the server list includes the
                // session; until then mergeInflight keeps it visible (#225, #175).
                if (sid !== null && list.some((t) => t.id === sid)) {
                  setInflightThread(null);
                }
              });
            },
            onError: (msg, code) => {
              const turnId = streamingTurnIdRef.current;
              if (turnId !== null) {
                updateTurnById(turnId, streamingSessionIdRef.current, (last) => ({
                  ...last,
                  template: "UNCERTAIN",
                  directAnswer: `（出错 ${code ?? "ERROR"}）${msg}`,
                  streaming: false
                }));
              }
              setStreaming(false);
              streamingTurnIdRef.current = null;
            }
          },
          abort.signal,
          images,
          getSelectedModelId(),
          undefined,
          mention,
          regenerateArtifactId
        );
      };

      run().catch((err) => {
        console.error("streamChat threw", err);
        // Transport failure before any onError — surface a visible, retryable
        // message on the in-flight turn instead of leaving it spinning.
        const turnId = streamingTurnIdRef.current;
        if (turnId !== null) {
          updateTurnById(turnId, streamingSessionIdRef.current, (last) => ({
            ...last,
            template: "UNCERTAIN",
            directAnswer: "抱歉，连接出错了，请稍后重试。",
            streaming: false
          }));
        }
        setStreaming(false);
        streamingTurnIdRef.current = null;
      });
    },
    [activeSessionId, streaming, updateTurnById, commitStreamTurns]
  );

  // PH-1 (MEETING_SESSION): "问这场会" opens the meeting's OWN conversation (its
  // summary is already seeded there as context) and sends only the question.
  // Load the session first, then fire the send once it is active — sending
  // straight away would hit onSend's stale activeSessionId closure and target the
  // wrong thread. The effect below completes the send when the session is active.
  const onAskMeeting = useCallback((conversationId: string, question: string) => {
    setMeetingOpen(false);
    setPendingMeetingAsk({ sid: conversationId, q: question });
    void loadSession(conversationId).catch((err) => { console.error("load meeting session failed", err); });
  }, [loadSession]);

  useEffect(() => {
    if (pendingMeetingAsk === null) return;
    if (activeSessionId === pendingMeetingAsk.sid && !streaming) {
      onSend(pendingMeetingAsk.q);
      setPendingMeetingAsk(null);
    }
  }, [pendingMeetingAsk, activeSessionId, streaming, onSend]);

  // R★.2 guides handoff ("在小环里继续"): the manual reader stashes a context-
  // carrying message and navigates here; auto-send it ONCE so the conversation
  // continues in the main line with the manual context attached. Distinct from
  // onHandoff below (转人工 = human escalation).
  const guidesHandoffFiredRef = useRef(false);
  useEffect(() => {
    // Behind the login wall the message stays stashed — it is consumed on the
    // first authenticated render instead of being burned against a 401.
    if (guidesHandoffFiredRef.current || streaming || needsLogin) return;
    let msg: string | null = null;
    try {
      msg = sessionStorage.getItem("guides-handoff");
    } catch {
      return;
    }
    if (msg === null || msg.length === 0) return;
    guidesHandoffFiredRef.current = true;
    onSend(msg);
    try {
      sessionStorage.removeItem("guides-handoff");
    } catch {
      /* re-read is prevented by the fired ref for this mount */
    }
  }, [onSend, streaming, needsLogin]);

  const handoffPendingRef = useRef(false);
  const onHandoff = useCallback(() => {
    if (handoffPendingRef.current) return; // guard against double-click
    handoffPendingRef.current = true;
    abortRef.current?.abort();
    setStreaming(false);
    const sid = activeSessionIdRef.current;
    void postHandoff(sid).then((r) => {
      handoffPendingRef.current = false;
      // On success use the server's canonical, persisted text (§20.7.1) so the
      // live turn matches what a reload reconstructs; fall back for older
      // servers or the failure case.
      const text = r.ok
        ? (r.text ??
          (r.number !== undefined
            ? `好的，已经帮您联系人工客服啦～ 同事会通过邮件尽快回复您。您可以在左侧「我的工单」随时查看进度（#${r.number}），也可以继续在这儿告诉我，补充更多细节。`
            : "好的，已经帮您联系人工客服啦～ 同事会通过邮件尽快回复您。您也可以继续在这儿告诉我，补充更多细节。"))
        : `抱歉，暂时没能帮您联系上人工客服（${r.reason ?? "未配置"}）。您可以稍后再试，或直接联系您的对接人。`;
      const turn: AssistantTurn = {
        id: `handoff_${Date.now()}`,
        role: "assistant",
        template: "HANDOFF",
        directAnswer: text,
        citations: [],
        toolCalls: [],
        timestamp: Date.now(),
        streaming: false
      };
      setTurns((prev) => [...prev, turn]);
      // Refresh the sidebar so the thread shows the "已转人工" badge now that
      // the handoff is persisted server-side.
      if (r.ok) void fetchSessions().then(setThreads);
    });
  }, []);

  // Per-answer escalate (§2-1): reuse the handoff flow, and log which answer
  // prompted it so escalations are mineable alongside other feedback.
  const onEscalate = useCallback(
    (turnId: string) => {
      onHandoff();
      void postFeedback({ sessionId: activeSessionIdRef.current, kind: "escalate", turnId });
    },
    [onHandoff]
  );

  // Widget feedback bridge (§2-4): a reason chip in the resolution widget
  // reports why the answer did not help. Persisted for product-need mining;
  // best-effort, scoped to the session in view.
  const onFeedback = useCallback((reason: string, note?: string) => {
    void postFeedback({ sessionId: activeSessionIdRef.current, kind: "unresolved", reason, note });
  }, []);

  // Answer-action row (§2-1): thumbs rating (+ optional picked reason / note)
  // and one-click escalate. Both feed the same feedback log as §2-4.
  const onRate = useCallback(
    (
      turnId: string,
      rating: "up" | "down" | null,
      reasons?: string[],
      note?: string,
      screenshot?: { data: string; mimeType: string }
    ) => {
      // Resolve the rated turn's question + answer so the server can persist the
      // rating under a stable content signature (live turn ids are ephemeral, so
      // turnId alone cannot be re-mapped on reload). Read from a ref to avoid a
      // stale closure without re-creating the callback on every turn.
      const cur = turnsRef.current;
      const idx = cur.findIndex((t) => t.role === "assistant" && t.id === turnId);
      const answer = idx >= 0 ? (cur[idx] as AssistantTurn).directAnswer : undefined;
      const question = idx >= 0 ? precedingQuestionAt(cur, idx) : undefined;
      // The rated turn's provider response id, the stable key that joins this
      // rating to its raw step record (openspec/rating-step-join.md).
      const responseId = idx >= 0 ? (cur[idx] as AssistantTurn).responseId : undefined;
      void postFeedback({
        sessionId: activeSessionIdRef.current,
        kind: "rating",
        rating,
        turnId,
        reasons,
        note,
        question,
        answer,
        responseId,
        screenshot
      });
    },
    []
  );

  // Resolution panel (Feature 2): persist a step-state change, then apply the
  // server's authoritative block to the turn. On failure, replace the block with
  // an identity copy so the panel clears its busy lock and re-enables buttons
  // without changing state.
  const onMutateStep = useCallback(
    (blockId: string, stepId: string, state: ResolutionStepState) => {
      const sessionId = activeSessionIdRef.current;
      if (sessionId === null) return;
      void mutateResolutionStep(sessionId, blockId, stepId, state).then((updated) => {
        setTurns((prev) =>
          prev.map((t) => {
            if (t.role !== "assistant" || t.blocks === undefined) return t;
            if (!t.blocks.some((b) => b.id === blockId)) return t;
            return {
              ...t,
              blocks: t.blocks.map((b) => (b.id === blockId ? (updated ?? { ...b }) : b))
            };
          })
        );
      });
    },
    []
  );

  const onStop = useCallback(() => {
    abortRef.current?.abort();
    // The aborted stream never emits `done`, so the in-flight assistant turn
    // would stay `streaming: true` forever (blinking cursor, no settle). Clear
    // it explicitly before dropping the ref (codex 2026-06-02 §3).
    const turnId = streamingTurnIdRef.current;
    if (turnId !== null) {
      updateTurnById(turnId, streamingSessionIdRef.current, (last) => ({
        ...last,
        streaming: false
      }));
    }
    setStreaming(false);
    streamingTurnIdRef.current = null;
  }, [updateTurnById]);

  const onDeleteThread = useCallback((id: string) => {
    // Drop the optimistic pin too, so a deleted in-flight session does not get
    // re-pinned into the sidebar by mergeInflight (#225).
    setInflightThread((cur) => (cur?.id === id ? null : cur));
    void deleteSession(id)
      .then(() => fetchSessions())
      .then(setThreads)
      .catch((err) => console.error("delete session failed", err));
    // If the deleted thread is open, clear the view.
    if (id === activeSessionIdRef.current) {
      setActiveSessionId(null);
      activeSessionIdRef.current = null;
      setTurns([]);
      setActiveCitation(null);
    }
  }, []);

  // The sidebar list with any not-yet-persisted streaming session pinned in, so
  // a started chat is always reachable from the left nav (#225, #175).
  const sidebarThreads = mergeInflight(threads, inflightThread);
  const activeThread = sidebarThreads.find((t) => t.id === activeSessionId) ?? null;

  // Staged @filename references (P4a): chips above the composer, removable,
  // sent with the next message so retrieval pins to those personal files.
  const health = healthState.health;
  // Replay feature gate (FEATURE_IA_REPLAY): the host exposes it via health
  // `features.replay` so the playbook can gate its「看真实案例回放」affordance
  // without hardcoding the flag.
  const replayEnabled = health?.features?.replay === true;
  // Agent-profile M2: the serving assistant's identity (rides the start SSE
  // event, server-rendered) + the T4-filtered @-popover feed. Both are empty
  // when FEATURE_AGENT_PROFILE is off, so nothing renders.
  const [servingAssistant, setServingAssistant] = useState<{
    profileId: string;
    name: string;
  } | null>(null);
  const [mentionables, setMentionables] = useState<MentionableAssistant[]>([]);
  // 换回 (§09): remembers who was serving before a transfer switched the pill.
  const [returnAssistant, setReturnAssistant] = useState<{
    profileId: string;
    name: string;
  } | null>(null);
  // Preset @ mention staged from outside the composer (换回 affordance).
  const [presetMention, setPresetMention] = useState<MentionableAssistant | null>(null);
  // Capability card overlay (pill click-through; server-rendered content).
  const [assistantCard, setAssistantCard] = useState<AssistantCardData | null>(null);
  const [assistantCardError, setAssistantCardError] = useState(false);
  // Which session the current pill belongs to (stamped by the stream's
  // start/assistant events) — lets the per-thread reset below distinguish a
  // REAL thread switch from the first turn of a new chat adopting its
  // server-assigned id (same thread; the pill must survive).
  const pillSessionRef = useRef<string | null>(null);
  // The pill is per-thread state: switching threads (or starting a new chat)
  // must not carry the previous thread's serving assistant / 换回 over.
  useEffect(() => {
    if (activeSessionId !== null && activeSessionId === pillSessionRef.current) return;
    pillSessionRef.current = null;
    setServingAssistant(null);
    setReturnAssistant(null);
    setAssistantCard(null);
    setAssistantCardError(false);
  }, [activeSessionId]);
  useEffect(() => {
    if (health?.features?.agentProfile !== true) return;
    void getMentionableAssistants().then(setMentionables);
  }, [health?.features?.agentProfile]);
  // Which product's manual the reader shows (configurable). The sidebar label
  // carries the product name (e.g. "使用手册 · 织灵") from that manual's manifest.
  const manualId = health?.manualReaderManual ?? "cyclecare";
  const [manualLabel, setManualLabel] = useState("使用手册");
  useEffect(() => {
    if (health?.features?.manualReader !== true) return;
    let live = true;
    void fetch(`/api/guides/${encodeURIComponent(manualId)}/toc`)
      .then((r) => (r.ok ? (r.json() as Promise<{ title?: string }>) : null))
      .then((t) => {
        if (live && t?.title) setManualLabel(t.title);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [manualId, health?.features?.manualReader]);

  if (needsLogin) {
    return (
      <Login
        onSuccess={() => {
          setUser(getUser());
          setNeedsLogin(false);
          loadData();
        }}
      />
    );
  }

  // FEATURE_ARTIFACTS: the 作品库 entry + card→canvas open path exist only when on.
  const artifactsEnabled = health?.features?.artifacts === true;
  const canvasOpen = artifactsEnabled && activeArtifactId !== null;

  // The artifact canvas is a split-view right column (chat shifts left) with its
  // own comfortable reading width — wider than the citation rail, and expandable.
  const canvasClass = canvasOpen
    ? `canvas-open${canvasExpanded ? " canvas-expanded" : ""}`
    : "";

  return (
    <div
      className={`app ${activeCitation !== null ? "rail-open" : ""} ${canvasClass} ${navOpen ? "nav-open" : ""}`}
    >
      <Topbar
        health={health}
        loaded={healthState.loaded}
        downReason={healthState.downReason}
        {...(servingAssistant !== null ? { assistantName: servingAssistant.name } : {})}
        {...(returnAssistant !== null && returnAssistant.profileId !== servingAssistant?.profileId
          ? {
              returnToName: returnAssistant.name,
              onReturnTo: () => {
                setPresetMention({
                  id: returnAssistant.profileId,
                  name: returnAssistant.name,
                  isDefault: false
                });
                setReturnAssistant(null);
              }
            }
          : {})}
        onAssistantClick={() => {
          if (servingAssistant === null) return;
          setAssistantCardError(false);
          void getAssistantCard(servingAssistant.profileId).then((c) => {
            if (c === null) setAssistantCardError(true);
            else setAssistantCard(c);
          });
        }}
        title={activeThread?.title ?? (turns.length === 0 ? "新对话" : "对话")}
        onToggleNav={() => setNavOpen((v) => !v)}
        onOpenLoom={() => setShowLoom(true)}
      />
      {assistantCardError && (
        <div className="assistant-card-overlay" data-testid="assistant-card" role="dialog">
          <div className="assistant-card">
            <div className="assistant-card-head">
              <strong>能力卡加载失败。</strong>
              <button aria-label="关闭" onClick={() => setAssistantCardError(false)}>
                ×
              </button>
            </div>
            <button
              onClick={() => {
                if (servingAssistant === null) return;
                setAssistantCardError(false);
                void getAssistantCard(servingAssistant.profileId).then((c) => {
                  if (c === null) setAssistantCardError(true);
                  else setAssistantCard(c);
                });
              }}
            >
              重试
            </button>
          </div>
        </div>
      )}
      {assistantCard !== null && (
        <div className="assistant-card-overlay" data-testid="assistant-card" role="dialog">
          <div className="assistant-card">
            <div className="assistant-card-head">
              <strong>{assistantCard.name}</strong>
              {assistantCard.tagline !== undefined && (
                <span className="assistant-card-tagline">{assistantCard.tagline}</span>
              )}
              <button aria-label="关闭" onClick={() => setAssistantCard(null)}>
                ×
              </button>
            </div>
            {assistantCard.sections.map((sec) => (
              <p key={sec.heading}>
                <strong>{sec.heading}</strong>：{sec.text}
              </p>
            ))}
            {assistantCard.starters.length > 0 && (
              <div className="assistant-card-starters">
                <strong>{assistantCard.startersHeading}</strong>
                {assistantCard.starters.map((q) => (
                  <button
                    key={q}
                    onClick={() => {
                      setAssistantCard(null);
                      onSend(q);
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
            <p className="assistant-card-foot">{assistantCard.footer}</p>
          </div>
        </div>
      )}
      <Sidebar
        threads={sidebarThreads}
        activeThreadId={activeSessionId}
        onSelectThread={(id) => {
          onSelectThread(id);
          setMeetingOpen(false);
          setNavOpen(false);
        }}
        onNewThread={() => {
          onNewThread();
          setMeetingOpen(false);
          setNavOpen(false);
        }}
        onDeleteThread={onDeleteThread}
        onOpenTickets={() => {
          setShowTickets(true);
          setNavOpen(false);
        }}
        user={user}
        onLogout={handleLogout}
        onOpenFeedback={() => {
          setShowFeedback(true);
          setNavOpen(false);
        }}
        manualReaderEnabled={health?.features?.manualReader === true}
        manualLabel={manualLabel}
        meetingEnabled={health?.features?.meeting === true && health?.features?.meetingCapture === true}
        settingsEnabled={health?.features?.settings === true}
        onOpenMeetings={() => {
          setMeetingOpen(true);
          setNavOpen(false);
        }}
        meetingActive={meetingOpen}
      />
      {navOpen && (
        <button
          className="nav-backdrop"
          type="button"
          aria-label="关闭菜单"
          onClick={() => setNavOpen(false)}
        />
      )}
      <main className="chat">
        {meetingOpen ? (
          <MeetingView onSendPrompt={(t) => { setMeetingOpen(false); onSend(t); }} onAskMeeting={onAskMeeting} dispatchEnabled={health?.features?.meetingDispatch === true} notesEnabled={health?.features?.meetingNotes === true} skeletonEnabled={health?.features?.meetingSkeleton === true} liveEnabled={health?.features?.meetingLive === true} sessionEnabled={health?.features?.meetingSession === true} importEnabled={health?.features?.meetingImport === true} vadEnabled={health?.features?.meetingVad === true} systemAudioEnabled={health?.features?.meetingSystemAudio === true} />
        ) : turns.length === 0 ? (
          // Home stage: greeting + composer + suggestions, centered (ui-kit
          // home-01-stage), not a bottom-pinned bar.
          <div className="home-stage">
            <div className="home-hero">
              <div className="home-hero-brand">
                <BrandLogo size={60} />
                <span className="home-hero-name">CycleCare · 小环</span>
              </div>
              <p className="home-hero-intro">
                {user?.role === "end_user" ? WELCOME_END_USER : WELCOME_STAFF}
              </p>
            </div>
            <Composer
              onSend={onSend}
              onHandoff={onHandoff}
              onStop={onStop}
              streaming={streaming}
              disabled={streaming}
              maxChars={MAX_INPUT_CHARS}
              assistants={mentionables}
              presetMention={presetMention}
              onPresetConsumed={() => setPresetMention(null)}
              personalMaterialsEnabled={health?.features?.personalMaterials === true}
              onOpenLibrary={() => setShowMaterials(true)}
            />
            {/* "About 小环 itself" line — kept OUT of the 织灵 chip row so the two
                are never confused (#90). 了解小环 triggers the capability card;
                Playbook opens the role-aware starter-question gallery. */}
            <div className="home-entries">
              <button
                type="button"
                className="home-entry"
                onClick={() => onSend(PINNED_SUGGESTION)}
                disabled={streaming}
              >
                <span className="home-entry-ic" aria-hidden="true">
                  <IconSpark size={18} />
                </span>
                <span className="home-entry-body">
                  <span className="home-entry-t">了解小环</span>
                  <span className="home-entry-d">一分钟看懂我能帮你做什么</span>
                </span>
                <IconArrowRight className="home-entry-go" size={16} aria-hidden="true" />
              </button>
              {hasPlaybook() && (
                <button
                  type="button"
                  className="home-entry"
                  onClick={() => setPlaybookOpen(true)}
                  disabled={streaming}
                >
                  <span className="home-entry-ic" aria-hidden="true">
                    <IconGrid size={18} />
                  </span>
                  <span className="home-entry-body">
                    <span className="home-entry-t">使用手册 · Playbook</span>
                    <span className="home-entry-d">挑个模板，填一句就能问</span>
                  </span>
                  <IconArrowRight className="home-entry-go" size={16} aria-hidden="true" />
                </button>
              )}
              {artifactsEnabled && (
                <button
                  type="button"
                  className="home-entry"
                  onClick={() => setShowLibrary(true)}
                  data-testid="open-artifact-library"
                >
                  <span className="home-entry-ic" aria-hidden="true">
                    <IconFile size={18} />
                  </span>
                  <span className="home-entry-body">
                    <span className="home-entry-t">作品库</span>
                    <span className="home-entry-d">小环为你生成的文档，都在这里</span>
                  </span>
                  <IconArrowRight className="home-entry-go" size={16} aria-hidden="true" />
                </button>
              )}
            </div>
            {/* 织灵 (the supported product) starter questions — subject-qualified,
                no 小环-self question mixed in (#90). */}
            <div className="empty-suggestions-label">关于织灵（你咨询的产品）</div>
            <div className="empty-suggestions">
              {suggestions
                // 织灵-only row: drop the pinned self-question AND any other
                // 小环-self suggestion the KB generator may surface (#90).
                .filter((s) => s !== PINNED_SUGGESTION && !s.includes("小环"))
                .slice(0, 4)
                .map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="suggestion-chip"
                    onClick={() => onSend(s)}
                    disabled={streaming}
                  >
                    {s}
                  </button>
                ))}
            </div>
          </div>
        ) : (
          <>
            <div className="thread" ref={threadRef} onScroll={onThreadScroll}>
              {turns.map((t, idx) =>
                t.role === "user" ? (
                  <UserBubble key={t.id} turn={t} />
                ) : (
                  <AssistantBubble
                    key={t.id}
                    turn={t}
                    activeCitationId={activeCitation?.id ?? null}
                    onCitationClick={setActiveCitation}
                    onSendPrompt={onSend}
                    onFeedback={onFeedback}
                    onMutateStep={onMutateStep}
                    onSuggestionAction={onSuggestionAction}
                    {...(artifactsEnabled ? { onOpenArtifact: setActiveArtifactId } : {})}
                    precedingQuestion={precedingQuestionAt(turns, idx)}
                    {...(activeSessionId !== null ? { conversationId: activeSessionId } : {})}
                    onRate={onRate}
                    onEscalate={onEscalate}
                  />
                )
              )}
              {showJump && (
                <button
                  type="button"
                  className="scroll-down-btn"
                  aria-label="回到最新消息"
                  onClick={() => scrollThreadToBottom("smooth")}
                >
                  <IconArrowDown size={18} />
                </button>
              )}
            </div>
            <Composer
              onSend={onSend}
              onHandoff={onHandoff}
              onStop={onStop}
              streaming={streaming}
              disabled={streaming}
              maxChars={MAX_INPUT_CHARS}
              assistants={mentionables}
              presetMention={presetMention}
              onPresetConsumed={() => setPresetMention(null)}
              personalMaterialsEnabled={health?.features?.personalMaterials === true}
              onOpenLibrary={() => setShowMaterials(true)}
            />
          </>
        )}
      </main>
      {activeCitation !== null && (
        <RightRail citation={activeCitation} onClose={() => setActiveCitation(null)} />
      )}
      {canvasOpen && activeArtifactId !== null && (
        <ArtifactCanvas
          artifactId={activeArtifactId}
          onClose={() => {
            setActiveArtifactId(null);
            setCanvasExpanded(false);
          }}
          promotable={health?.features?.personalMaterials === true}
          expanded={canvasExpanded}
          onToggleExpand={() => setCanvasExpanded((v) => !v)}
          onRegenerate={(id, title) => {
            // Regenerate = a chat turn that re-produces the doc; the create_artifact
            // it triggers APPENDS a version to THIS id (regenerateArtifactId). Close
            // the canvas so the user watches it regenerate in the conversation.
            // (✦AI润色 does NOT go through chat — it's a server-side transform on the
            // draft inside the canvas, bypassing the chat 4000-char cap.)
            setActiveArtifactId(null);
            setCanvasExpanded(false);
            onSend(
              `请把《${title}》这份文档重新整理、更新一版，让内容更完整准确，并保存为该文档的新版本。`,
              undefined,
              undefined,
              id
            );
          }}
        />
      )}
      {showTickets && (
        <TicketsPanel onClose={() => setShowTickets(false)} role={user?.role ?? null} />
      )}
      {showLoom && <LoomPanel onClose={() => setShowLoom(false)} />}
      {showMaterials && <PersonalKbPanel onClose={() => setShowMaterials(false)} />}
      {showLibrary && (
        <ArtifactLibraryPanel
          onClose={() => setShowLibrary(false)}
          onOpen={(id) => {
            setActiveArtifactId(id);
            setShowLibrary(false);
          }}
        />
      )}
      {showFeedback && (
        <FeedbackModal onClose={() => setShowFeedback(false)} conversationId={activeSessionId} />
      )}
      <PlaybookOverlay
        open={playbookOpen}
        onClose={() => setPlaybookOpen(false)}
        onLaunch={(prompt, images) => onSend(prompt, images)}
        onAction={(action) => {
          if (action === "feedback") {
            setPlaybookOpen(false);
            setShowFeedback(true);
          }
        }}
        replayEnabled={replayEnabled}
        features={health?.features ?? {}}
        onOpenReplay={(replays, title) => setReplaySession({ replays, title })}
      />
      {replayEnabled && replaySession !== null && (
        <ReplaySessionView
          replays={replaySession.replays}
          onClose={() => setReplaySession(null)}
          loomLiveEnabled={health?.features?.loomLive === true}
        />
      )}
    </div>
  );
}

import { RequireAdmin } from "./console/RequireAdmin";
import { SettingsView, MeetingsSettings } from "./settings/SettingsView";
import { RequireReviewer } from "./review/RequireReviewer";
import { ReviewConsole } from "./review/ReviewConsole";
import { GuidesReader } from "./guides/GuidesReader";
import { SchedulerPage } from "./scheduler/SchedulerPage";
import { AdminConsole } from "./console/AdminConsole";
import { OverviewSection } from "./console/sections/OverviewSection";
import { TrafficSection } from "./console/sections/TrafficSection";
import { InsightsSection } from "./console/sections/InsightsSection";
import { UsageSection } from "./console/sections/UsageSection";
import { SystemSection } from "./console/sections/SystemSection";
import { UsersSection } from "./console/sections/UsersSection";
import { UserDetailSection } from "./console/sections/UserDetailSection";
import { KbSourcesSection } from "./console/sections/KbSourcesSection";
import { ErrorsSection } from "./console/sections/ErrorsSection";
import { LogsSection } from "./console/sections/LogsSection";
import { DeploymentsSection } from "./console/sections/DeploymentsSection";
import { ActivitySection } from "./console/sections/ActivitySection";
import { BackupsSection } from "./console/sections/BackupsSection";
import { ConversationView } from "./console/sections/ConversationView";
import { ProductFeedbackSection } from "./console/sections/ProductFeedbackSection";
import { ProfilesSection } from "./console/sections/ProfilesSection";

export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<App />} />
      <Route path="/shared/artifact/:token" element={<SharedArtifactView />} />
      <Route path="/guides" element={<GuidesReader />} />
      <Route path="/guides/:manual" element={<GuidesReader />} />
      <Route path="/guides/:manual/:pageId" element={<GuidesReader />} />
      {/* FEATURE_SETTINGS audit surface (openspec/settings-surface.md). The
          view checks the flag + auth itself via /healthz and redirects home
          when dark, so no route-level guard component is needed. */}
      <Route path="/settings" element={<SettingsView />}>
        <Route index element={<Navigate to="/settings/meetings" replace />} />
        <Route path="meetings" element={<MeetingsSettings />} />
      </Route>
      <Route
        path="/review"
        element={
          <RequireReviewer>
            <ReviewConsole />
          </RequireReviewer>
        }
      />
      <Route
        path="/scheduler"
        element={
          <RequireAdmin>
            <SchedulerPage />
          </RequireAdmin>
        }
      />
      <Route
        path="/admin"
        element={
          <RequireAdmin>
            <AdminConsole />
          </RequireAdmin>
        }
      >
        <Route index element={<Navigate to="/admin/overview" replace />} />
        <Route path="overview" element={<OverviewSection />} />
        <Route path="traffic" element={<TrafficSection />} />
        <Route path="insights" element={<InsightsSection />} />
        <Route path="usage" element={<UsageSection />} />
        <Route path="system" element={<SystemSection />} />
        <Route path="users" element={<UsersSection />} />
        <Route path="users/:userId" element={<UserDetailSection />} />
        <Route path="knowledge" element={<KbSourcesSection />} />
        <Route path="errors" element={<ErrorsSection />} />
        <Route path="logs" element={<LogsSection />} />
        <Route path="activity" element={<ActivitySection />} />
        <Route path="product-feedback" element={<ProductFeedbackSection />} />
        <Route path="assistants" element={<ProfilesSection />} />
        {/* P2d-4: assistant detail page (builder flag on; flag off renders the P1 single page) */}
        <Route path="assistants/:profileId" element={<ProfilesSection />} />
        <Route path="backups" element={<BackupsSection />} />
        <Route path="deployments" element={<DeploymentsSection />} />
        <Route path="conversations/:sessionId" element={<ConversationView />} />
      </Route>
    </Routes>
  );
}
